import { useState, useCallback, useMemo } from "react";
import {
  ChevronRight,
  ChevronDown,
  HeartPulse,
  Activity,
  Sparkles,
} from "lucide-react";
import { services } from "../data/servicio";
import { palette } from "../style_palete";

const iconComponents = {
  HeartPulse,
  Activity,
  Sparkles,
};

const ServiceCard = ({ service, isExpanded, onToggle }) => {
  const IconComponent = iconComponents[service.icon];

  return (
    <div className="rounded-2xl border border-slate-200/70 bg-white/70 backdrop-blur overflow-hidden shadow-sm hover:shadow-lg transition">
      {/* Layout horizontal - IGUAL EN TODOS LOS DISPOSITIVOS */}
      <div className="flex h-64">
        <div className="w-2/6 h-full">
          <img
            src={service.image}
            alt={service.title}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        </div>

        <div className="w-4/6 p-5 flex flex-col">
          <div className="flex items-center gap-3">
            <div
              className="h-10 w-10 rounded-xl flex items-center justify-center text-white shadow"
              style={{
                background: `linear-gradient(135deg, ${palette.primary}, ${palette.teal})`,
              }}
            >
              <IconComponent className="h-5 w-5" />
            </div>

            <h3 className="font-semibold text-lg text-[#0F1B2D]">
              {service.title.startsWith("MEP") ? (
                <>
                  <span translate="no">MEP</span> (Microelectrólisis Percutánea)
                </>
              ) : (
                service.title
              )}
            </h3>
          </div>

          <p className="mt-3 text-slate-600 text-base leading-relaxed">
            {service.desc}
          </p>

          <button
            type="button"
            className="mt-4 inline-flex items-center gap-2 text-base font-semibold transition-transform hover:scale-105 self-start"
            style={{ color: palette.primary }}
            onClick={onToggle}
          >
            {isExpanded ? (
              <>
                Ver menos <ChevronDown className="h-5 w-5" />
              </>
            ) : (
              <>
                Saber más <ChevronRight className="h-5 w-5" />
              </>
            )}
          </button>
        </div>
      </div>

      {/* Contenido expandido */}
      {isExpanded && (
        <div className="p-5 border-t border-slate-200/70 bg-[#f7fcff]">
          <p className="text-slate-700 text-base leading-relaxed whitespace-pre-line">
            {service.more}
          </p>
        </div>
      )}
    </div>
  );
};

export default function Services() {
  const [showMore, setShowMore] = useState(false);
  const [expandedKey, setExpandedKey] = useState(null);

  const handleToggleCard = useCallback((key) => {
    setExpandedKey((prev) => (prev === key ? null : key));
  }, []);

  const handleShowMore = useCallback(() => {
    setShowMore((prev) => !prev);
    setExpandedKey(null);
  }, []);

  const mainServices = useMemo(() => services.slice(0, 6), []);
  const additionalServices = useMemo(() => services.slice(6), []);

  return (
    <section id="servicios" className="py-16">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        {/* Título */}
        <div className="max-w-2xl">
          <div className="mb-6">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-[#0F1B2D] mb-3">
              Nuestras Especialidades
            </h2>
            <div className="w-36 h-1.5 bg-[#2E8BC0] rounded-full"></div>
          </div>
          <p className="text-[#5E738A] text-base md:text-lg mt-4 md:mt-5">
            Abordamos tu recuperación de manera integral. Elegimos las técnicas
            adecuadas para tu diagnóstico y tu momento del proceso.
          </p>
        </div>

        {/* Servicios principales */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
          {mainServices.map((service, index) => {
            const key = `main-${index}`;
            return (
              <ServiceCard
                key={service.title}
                service={service}
                isExpanded={expandedKey === key}
                onToggle={() => handleToggleCard(key)}
              />
            );
          })}
        </div>

        {/* Ver más */}
        <div className="mt-12 flex justify-center items-center gap-3">
          <div
            className="h-12 w-12 md:h-12 md:w-12 flex items-center justify-center cursor-pointer transition hover:scale-105 rounded-full border-2"
            style={{ borderColor: palette.primarySoft, color: palette.primary }}
            onClick={handleShowMore}
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d={showMore ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"}
              />
            </svg>
          </div>

          <button
            type="button"
            onClick={handleShowMore}
            className="font-medium cursor-pointer hover:underline transition text-base md:text-lg px-3 py-2 rounded-lg active:bg-gray-100"
            style={{ color: palette.primary }}
          >
            Ver {showMore ? "menos" : "más"} especialidades
          </button>
        </div>

        {/* Servicios adicionales */}
        {showMore && (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
            {additionalServices.map((service, index) => {
              const key = `add-${index}`;
              return (
                <ServiceCard
                  key={service.title}
                  service={service}
                  isExpanded={expandedKey === key}
                  onToggle={() => handleToggleCard(key)}
                />
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
